import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useApi } from '../_shared/hooks';
import { fetchGongFlowSteps, replyStepOrdersOf } from './gong-flow-picker-api';
import { gongFlowStepsQueryKey } from './query-keys';
import type { LauncherChannel } from './schemas';

/**
 * The bound Gong flow's live reply-step ordinals, for whichever editing
 * surface is rendering a sequence's steps.
 *
 * WHY A SHARED HOOK rather than two inline `useQuery`s: two hosts render
 * `SequenceStepEditor` (`play-flow/SequencesStep.tsx`'s `SequenceEditorShell`
 * and the Library page's `SequenceCopyPane.tsx`), and they must agree with
 * each other AND with the send. One hook means there is one place the query
 * key, the enablement rule, and the "shape unknown" answer can be wrong.
 *
 * The answer feeds `isReplyAtOrdinal` (`gong-flow-picker-api.ts`), which is
 * the editor's half of the ONE reply-step rule — see that module's
 * "reply-step contract" note for why the step's own `gongStepDescriptor` is
 * NOT the authority, and `send-payload.ts#toOverrideSteps` for the send's half.
 *
 * Empty set = "this flow's shape is unknown" (non-Gong channel, no flow bound,
 * steps still loading, or the fetch failed). Every step keeps its Subject,
 * matching `toOverrideSteps`'s own "no `targetFlow`, change nothing" posture:
 * showing a field the send might drop is recoverable, hiding one the send
 * transmits is not.
 */

/** Frozen module-scope constant, so the "unknown shape" return is reference-stable across renders. */
const NO_REPLY_ORDERS: ReadonlySet<number> = new Set<number>();

export function useGongReplyStepOrders(
  channel: LauncherChannel | undefined,
  flowId: string | null,
): ReadonlySet<number> {
  const { apiFetch } = useApi();
  // Only `gong_engage` has flows at all, and only a BOUND sequence has an id
  // to ask about. Every other case is the unknown-shape answer below, with no
  // request issued.
  const enabled = channel === 'gong_engage' && flowId !== null;

  const stepsQuery = useQuery({
    // Same `['playbooks', …]` namespace as every other query in this app, keyed
    // by flow rather than by sequence: two sequences bound to the same flow
    // share the one cache entry.
    queryKey: gongFlowStepsQueryKey(flowId),
    // `GET /api/flow/gong/flows/:flowId/steps` is MIRROR-FIRST with a live Gong
    // fallback that UPSERTS what it fetched. That side effect matters here:
    // the send reads `replyStepOrders` from the `gong_flows` mirror ONLY
    // (`toTargetFlowShape`, via `getGongFlowStepShapes`) and changes nothing
    // when the mirror has never synced the flow — so a rep merely opening the
    // step editor is also what populates the mirror the send later depends on.
    // Not the reason this query exists, but a real reason not to make it
    // lazier than it is.
    queryFn: () => fetchGongFlowSteps(apiFetch, flowId as string),
    enabled,
  });

  return useMemo(
    () => (stepsQuery.data === undefined ? NO_REPLY_ORDERS : replyStepOrdersOf(stepsQuery.data.steps)),
    [stepsQuery.data],
  );
}
